import { useCallback, useEffect, useRef, useState } from "react"

import type { ProductGroups } from "../../../../../modules/merchant/admin/products/productsTypes"

type UseShopScrollGroupsResult = Readonly<{
    rootRef: React.RefObject<HTMLDivElement>
    activeGroupId: number | null
    setActiveGroupId: (id: number | null) => void
    scrollToGroup: (groupId: number) => void
}>

// How long (ms) to suppress the IntersectionObserver after a manual category click
const SCROLL_LOCK_MS = 600

export function useShopScrollGroups(groups: ProductGroups[], isFavoriteFilter: boolean): UseShopScrollGroupsResult {
    const rootRef = useRef<HTMLDivElement>(null)
    const [activeGroupId, setActiveGroupId] = useState<number | null>(null)
    const scrollLockRef = useRef(false)
    const scrollLockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (isFavoriteFilter || activeGroupId !== null) return
        const first = groups[0]
        if (first) setActiveGroupId(first.id)
    }, [groups, isFavoriteFilter, activeGroupId])

    // Scroll-driven tab highlighting via IntersectionObserver
    useEffect(() => {
        const container = rootRef.current
        if (!container || isFavoriteFilter) return

        const observer = new IntersectionObserver(
            (entries) => {
                // Suppress observer updates while user-initiated scroll is in progress
                if (scrollLockRef.current) return

                // Collect all currently intersecting group IDs, pick the topmost
                const intersecting = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

                if (intersecting.length > 0) {
                    const id = Number(intersecting[0].target.id.replace("group-", ""))
                    if (!isNaN(id)) setActiveGroupId(id)
                }
            },
            {
                root: container,
                // Keep only the upper half of the scroll container as the observation zone
                rootMargin: "0px 0px -50% 0px",
                threshold: 0,
            },
        )

        groups.forEach((group) => {
            const el = document.getElementById(`group-${group.id}`)
            if (el) observer.observe(el)
        })

        return () => observer.disconnect()
    }, [groups, isFavoriteFilter])

    const scrollToGroup = useCallback((groupId: number) => {
        const el = document.getElementById(`group-${groupId}`)
        const container = rootRef.current
        if (!el || !container) return

        // Update active tab immediately on click, then lock the observer during scroll
        setActiveGroupId(groupId)
        scrollLockRef.current = true
        if (scrollLockTimerRef.current) clearTimeout(scrollLockTimerRef.current)
        scrollLockTimerRef.current = setTimeout(() => {
            scrollLockRef.current = false
        }, SCROLL_LOCK_MS)

        const containerRect = container.getBoundingClientRect()
        const elRect = el.getBoundingClientRect()
        container.scrollTo({
            top: container.scrollTop + elRect.top - containerRect.top - 16,
            behavior: "smooth",
        })
    }, [])

    return { rootRef, activeGroupId, setActiveGroupId, scrollToGroup }
}
